/**
 * Leader election via BroadcastChannel.
 * Ensures only one tab plays alerts / shows notifications.
 */

const CHANNEL_NAME = 'full-life-leader';
const HEARTBEAT_INTERVAL = 2000;
const HEARTBEAT_TIMEOUT = 5000;

class LeaderElection {
  private id = Math.random().toString(36).slice(2);
  private channel: BroadcastChannel | null = null;
  private leader = false;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private timeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private leaderId: string | null = null;

  initialize() {
    if (typeof BroadcastChannel === 'undefined') {
      // Fallback: always leader if BroadcastChannel unsupported
      this.leader = true;
      return;
    }

    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.onmessage = (e) => this.handleMessage(e.data);

    window.addEventListener('beforeunload', () => {
      if (this.leader) {
        this.channel?.postMessage({ type: 'leader-leaving', id: this.id });
      }
    });

    // Announce presence and trigger election
    this.startElection();
  }

  isLeader(): boolean {
    return this.leader;
  }

  private handleMessage(msg: { type: string; id: string }) {
    switch (msg.type) {
      case 'heartbeat':
        if (msg.id === this.leaderId) {
          this.resetTimeout();
        }
        break;
      case 'election':
        // Respond if our ID is higher
        if (this.id > msg.id) {
          this.startElection();
        }
        break;
      case 'leader':
        this.leaderId = msg.id;
        if (msg.id === this.id) {
          this.becomeLeader();
        } else {
          this.leader = false;
          this.stopHeartbeat();
          this.resetTimeout();
        }
        break;
      case 'leader-leaving':
        if (msg.id === this.leaderId) {
          this.startElection();
        }
        break;
    }
  }

  private startElection() {
    this.channel?.postMessage({ type: 'election', id: this.id });

    // Wait briefly for higher-ID tabs to respond, then claim leadership
    setTimeout(() => {
      // If no one with a higher ID claimed, we win
      if (!this.leader) {
        this.leaderId = this.id;
        this.channel?.postMessage({ type: 'leader', id: this.id });
        this.becomeLeader();
      }
    }, 300);
  }

  private becomeLeader() {
    this.leader = true;
    this.leaderId = this.id;
    this.startHeartbeat();
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.channel?.postMessage({ type: 'heartbeat', id: this.id });
    }, HEARTBEAT_INTERVAL);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private resetTimeout() {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
    }
    this.timeoutTimer = setTimeout(() => {
      // Leader timed out, start new election
      this.startElection();
    }, HEARTBEAT_TIMEOUT);
  }
}

export const leaderElection = new LeaderElection();
